import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';

export interface RedactedImage {
  original: File
  redacted: File
}

@Component({
    selector: 'app-image-redact',
    templateUrl: './image-redact.component.html',
    standalone: false
})
export class ImageRedactComponent implements OnChanges {

  @Input() file: Blob | null = null
  @Input() open = false
  @Output() saved = new EventEmitter<RedactedImage>()
  @Output() closed = new EventEmitter<void>()

  @ViewChild('canvas') canvasRef: ElementRef<HTMLCanvasElement>

  brushSize = 4
  saving = false

  private ctx: CanvasRenderingContext2D
  private drawing = false
  private lastX = 0
  private lastY = 0
  private history: ImageData[] = []
  private loadAttempts = 0

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue && this.file) {
      this.loadAttempts = 0
      this.scheduleLoad()
    }
  }

  private scheduleLoad() {
    setTimeout(() => {
      if (!this.canvasRef && this.loadAttempts < 10 && this.open) {
        this.loadAttempts++
        this.scheduleLoad()
        return
      }
      this.loadImage()
    }, 40)
  }

  loadImage() {
    if (!this.file || !this.canvasRef) {
      return
    }
    const url = URL.createObjectURL(this.file)
    const image = new Image()
    image.onload = () => {
      const canvas = this.canvasRef.nativeElement
      const scale = Math.min(1, 1400 / image.width, 1400 / image.height)
      canvas.width = Math.max(1, Math.round(image.width * scale))
      canvas.height = Math.max(1, Math.round(image.height * scale))
      this.ctx = canvas.getContext('2d')
      this.ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
      this.history = []
      URL.revokeObjectURL(url)
    }
    image.src = url
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (!this.open || !(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z' || event.shiftKey) {
      return
    }
    event.preventDefault()
    this.undo()
  }

  onPointerDown(event: PointerEvent) {
    if (!this.ctx) {
      return
    }
    const point = this.point(event)
    this.pushHistory()
    this.drawing = true
    this.lastX = point.x
    this.lastY = point.y
    event.preventDefault()
    this.canvasRef.nativeElement.setPointerCapture(event.pointerId)
    this.paint(point.x, point.y)
  }

  onPointerMove(event: PointerEvent) {
    if (!this.drawing) {
      return
    }
    const point = this.point(event)
    this.strokeTo(point.x, point.y)
    this.lastX = point.x
    this.lastY = point.y
  }

  onPointerUp() {
    this.drawing = false
  }

  undo() {
    const previous = this.history.pop()
    if (!previous || !this.ctx) {
      return
    }
    this.ctx.putImageData(previous, 0, 0)
  }

  reset() {
    this.loadImage()
  }

  confirm() {
    const canvas = this.canvasRef?.nativeElement
    const source = this.file
    if (!canvas || !source || this.saving) {
      return
    }
    const original = this.asFile(source)
    this.saving = true
    canvas.toBlob(blob => {
      this.saving = false
      if (!blob) {
        return
      }
      const redacted = new File([blob], this.redactedName(original.name), {type: 'image/jpeg'})
      this.saved.emit({original, redacted})
    }, 'image/jpeg', 0.9)
  }

  cancel() {
    this.closed.emit()
  }

  private strokeTo(x: number, y: number) {
    const distance = Math.hypot(x - this.lastX, y - this.lastY)
    const step = Math.max(2, Number(this.brushSize) / 3)
    const steps = Math.max(1, Math.ceil(distance / step))
    for (let i = 1; i <= steps; i++) {
      const px = this.lastX + ((x - this.lastX) * i) / steps
      const py = this.lastY + ((y - this.lastY) * i) / steps
      this.paint(px, py)
    }
  }

  private paint(x: number, y: number) {
    this.stampBlur(x, y)
  }

  private stampBlur(x: number, y: number) {
    const canvas = this.canvasRef.nativeElement
    const radius = Number(this.brushSize)
    const sx = Math.max(0, Math.floor(x - radius))
    const sy = Math.max(0, Math.floor(y - radius))
    const sw = Math.min(canvas.width - sx, Math.ceil(radius * 2))
    const sh = Math.min(canvas.height - sy, Math.ceil(radius * 2))
    if (sw < 2 || sh < 2) {
      return
    }
    const sample = Math.max(2, Math.round(Math.min(sw, sh) / 8))
    const tmp = document.createElement('canvas')
    tmp.width = sample
    tmp.height = sample
    const tmpCtx = tmp.getContext('2d')
    tmpCtx.imageSmoothingEnabled = true
    tmpCtx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sample, sample)
    this.ctx.save()
    this.ctx.beginPath()
    this.ctx.arc(x, y, radius, 0, Math.PI * 2)
    this.ctx.clip()
    this.ctx.imageSmoothingEnabled = true
    this.ctx.drawImage(tmp, 0, 0, sample, sample, sx, sy, sw, sh)
    this.ctx.restore()
  }

  private point(event: PointerEvent) {
    const canvas = this.canvasRef.nativeElement
    const rect = canvas.getBoundingClientRect()
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height)
    }
  }

  private pushHistory() {
    const canvas = this.canvasRef.nativeElement
    this.history.push(this.ctx.getImageData(0, 0, canvas.width, canvas.height))
    if (this.history.length > 12) {
      this.history.shift()
    }
  }

  private asFile(source: Blob): File {
    if (source instanceof File) {
      return source
    }
    const named = source as Blob & { name?: string }
    return new File([source], named.name || 'imagem', {type: source.type || 'image/jpeg'})
  }

  private redactedName(name: string) {
    const dot = name.lastIndexOf('.')
    const base = dot > 0 ? name.slice(0, dot) : name
    return base + '-oculto.jpg'
  }
}
